import { useEffect, useRef } from 'react'
import { useModals } from 'almanac-ui/Modal'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { channelTypeFromProvider } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { useHistory, generatePath, Link } from 'react-router-dom'
import { DESIGN_PATH, DOC_PATH, VIDEO_PATH } from 'paths'
import { EditCaptionButton } from '../EditPostMenu/EditCaptionButton'
import { ActionButton } from '../EditPostMenu/EditPostMenu'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { ImageEditor } from '../ImageEditor/ImageEditor'
import { AdjustPrivacySettingsModal } from 'components/AdjustPrivacySettingsModal'
import { AudioEditorModal } from 'components/Previews/components/VideoPlayer/AudioEditorModal'
import { Caption, FileEdit2, Microphone, AddElements, AudioSettings, Edit1, Wrench } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { useQueryClient } from 'react-query'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { removeFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { isStoryChannel } from '../utils'
import { ContentVariant } from './Sidebar'
import { MediaObject } from 'types'
import Styles from './Sidebar.module.scss'

interface QuickEditsSectionProps {
  scheduledPost: ScheduledPostShow
  almanacFile: AlmanacFileShowItem
  previewPlatform?: ExternalIntegrationEnum
  contentVariant: ContentVariant
  isBlankDesign?: boolean
  isInsertingMedia?: boolean
  onClose?: () => void
}

export const QuickEditsSection = observer(
  ({
    scheduledPost,
    almanacFile,
    previewPlatform,
    contentVariant,
    isBlankDesign = false,
    isInsertingMedia = false,
    onClose,
  }: QuickEditsSectionProps) => {
    const { generationTimestampStore } = useRootStore()
    const { openModal, closeAllModals } = useModals()
    const history = useHistory()
    const queryClient = useQueryClient()
    const workspaceId = useCurrentWorkspaceId()

    const replaceFrameBatchId = generationTimestampStore.getReplaceFrameBatchId(almanacFile.key)
    const isReplacingFrame = !!replaceFrameBatchId
    const isGenerating = almanacFile.is_generating || isReplacingFrame
    const isBusy = isGenerating || isInsertingMedia

    const isGenV2 = almanacFile.is_generation_v2

    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(
      () => () => {
        if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current)
      },
      [],
    )

    const shouldHideCaptionField =
      isStoryChannel(scheduledPost?.source_channel as DesignChannel) ||
      isStoryChannel(almanacFile?.channel as DesignChannel)

    const imageMediaObjects = (scheduledPost?.media_objects ?? []).filter(
      (obj: MediaObject) => obj.mime_type?.startsWith('image/') && obj.source_url && obj.width && obj.height,
    )

    const isVideoAsset = almanacFile.asset_type === 'Video'
    const editorPath = generatePath(isVideoAsset ? VIDEO_PATH : DESIGN_PATH, { key: almanacFile.key })

    const videoMediaObject = scheduledPost?.media_objects?.find((object) => object.mime_type?.startsWith('video/'))

    const handleOpenImageEditor = () => {
      openModal(
        ImageEditor,
        {
          designKey: almanacFile.key,
          mediaObjects: imageMediaObjects,
          channel: (previewPlatform ? channelTypeFromProvider[previewPlatform] : almanacFile.channel) as DesignChannel,
          integration: scheduledPost.external_integration,
          caption: scheduledPost.content,
        },
        { newStack: true },
      )
    }

    const handleAudioTransformComplete = () => {
      removeFileDetailCache(queryClient, workspaceId, almanacFile.key)
      queryClient.invalidateQueries({
        queryKey: calendarKeys.detail(almanacFile.key),
      })
    }

    const handleOpenAudioEditor = () => {
      if (!videoMediaObject) return
      openModal(
        AudioEditorModal,
        {
          mediaObject: videoMediaObject,
          almanacFileKey: almanacFile.key,
          onAudioTransformComplete: handleAudioTransformComplete,
        },
        { newStack: true },
      )
    }

    const handleOpenPrivacySettings = () => {
      openModal(AdjustPrivacySettingsModal, { scheduledPost }, { newStack: true })
    }

    const handleConfigureMetadata = () => {
      history.push({
        pathname: generatePath(DOC_PATH, { slug: almanacFile.key }),
      })
      closeTimerRef.current = setTimeout(() => onClose?.(), 100)
    }

    return (
      <>
        <div className={Styles.sectionLabel}>
          <Text variant="placeholder" color="var(--dark-40)">
            Quick Edits
          </Text>
        </div>
        <div className={Styles.quickEditsList}>
          {(contentVariant === 'social' || contentVariant === 'shortFormVideo') && !shouldHideCaptionField && (
            <EditCaptionButton
              scheduledPost={scheduledPost}
              almanacFileKey={almanacFile.key}
              variant="tertiary"
              size="md"
              frontIcon={Caption}
              justifyContent="flex-start"
              vertical={false}
              isDisabled={isGenerating}
              label="Adjust Caption"
              fullWidth
            />
          )}

          {contentVariant === 'social' &&
            imageMediaObjects.length > 0 &&
            !isBlankDesign &&
            isGenV2 &&
            !almanacFile.is_generation_v3 && (
              <ActionButton
                frontIcon={Edit1}
                isDisabled={isBusy}
                onPress={handleOpenImageEditor}
                fullWidth
                justifyContent="flex-start"
              >
                Change Design Elements
              </ActionButton>
            )}

          {contentVariant === 'shortFormVideo' && (
            <ActionButton
              frontIcon={AudioSettings}
              isDisabled={isGenerating}
              onPress={handleOpenPrivacySettings}
              fullWidth
              justifyContent="flex-start"
            >
              Adjust Privacy Settings
            </ActionButton>
          )}

          {videoMediaObject && !isBlankDesign && !isVideoAsset && (
            <ActionButton
              frontIcon={Microphone}
              isDisabled={isBusy}
              onPress={handleOpenAudioEditor}
              fullWidth
              justifyContent="flex-start"
            >
              Add Audio
            </ActionButton>
          )}

          {isVideoAsset && (
            <ButtonLink
              variant="tertiary"
              size="md"
              frontIcon={Edit1}
              elementType={Link}
              isDisabled={isBusy}
              to={editorPath}
              onPress={() => closeAllModals()}
              fullWidth
              justifyContent="flex-start"
            >
              Edit Video
            </ButtonLink>
          )}

          {(contentVariant === 'social' || contentVariant === 'shortFormVideo') && !isVideoAsset && (
            <ButtonLink
              variant="tertiary"
              size="md"
              frontIcon={AddElements}
              elementType={Link}
              isDisabled={isBusy}
              to={editorPath}
              onPress={() => closeAllModals()}
              fullWidth
              justifyContent="flex-start"
            >
              {isGenV2 && !almanacFile.is_generation_v3 && contentVariant === 'social'
                ? 'Add More in Designer'
                : 'Edit Design'}
            </ButtonLink>
          )}

          {(contentVariant === 'blog' || contentVariant === 'email') && (
            <ActionButton
              frontIcon={FileEdit2}
              onPress={() => {
                history.push(generatePath(DOC_PATH, { slug: almanacFile.key }))
                closeTimerRef.current = setTimeout(() => onClose?.(), 100)
              }}
              fullWidth
              justifyContent="flex-start"
            >
              Edit Document
            </ActionButton>
          )}

          {contentVariant === 'blog' && (
            <ActionButton frontIcon={Wrench} onPress={handleConfigureMetadata} fullWidth justifyContent="flex-start">
              Configure Metadata
            </ActionButton>
          )}
        </div>
      </>
    )
  },
)
